import React, { useState } from 'react';

const Resources = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Resources' },
    { id: 'guides', name: 'Study Guides' },
    { id: 'templates', name: 'Templates' },
    { id: 'tools', name: 'Tools & Calculators' },
    { id: 'articles', name: 'Articles' }
  ];

  const resources = [
    // Study Guides
    {
      id: 1,
      title: 'Complete Guide to Financial Modeling',
      category: 'guides',
      type: 'PDF Guide',
      description: 'Comprehensive 50-page guide covering 3-statement modeling, DCF valuation, and best practices.',
      downloadUrl: '#',
      size: '2.5 MB',
      downloads: 1250
    },
    {
      id: 2,
      title: 'Fixed Income Securities Handbook',
      category: 'guides', 
      type: 'PDF Guide',
      description: 'Essential concepts in bond valuation, yield curves, duration, and credit analysis.',
      downloadUrl: '#',
      size: '1.8 MB',
      downloads: 890
    },
    {
      id: 3,
      title: 'Python for Finance Cheat Sheet',
      category: 'guides',
      type: 'Quick Reference',
      description: 'Essential Python libraries and functions for financial analysis and modeling.',
      downloadUrl: '#',
      size: '0.5 MB',
      downloads: 2100
    },

    // Templates
    {
      id: 4,
      title: 'DCF Valuation Template',
      category: 'templates',
      type: 'Excel Template',
      description: 'Professional DCF model template with sensitivity analysis and scenario modeling.',
      downloadUrl: '#',
      size: '1.2 MB',
      downloads: 1800
    },
    {
      id: 5,
      title: 'Financial Dashboard Template',
      category: 'templates',
      type: 'Excel Template', 
      description: 'Dynamic financial dashboard with KPIs, charts, and automated calculations.',
      downloadUrl: '#',
      size: '2.1 MB',
      downloads: 950
    },
    {
      id: 6,
      title: 'Portfolio Optimization Template',
      category: 'templates',
      type: 'Excel Template',
      description: 'Mean-variance optimization template with risk-return analysis.',
      downloadUrl: '#',
      size: '1.5 MB',
      downloads: 720
    },

    // Tools
    {
      id: 7,
      title: 'Bond Price Calculator',
      category: 'tools',
      type: 'Online Tool',
      description: 'Calculate bond prices, YTM, and duration with this interactive calculator.',
      downloadUrl: '#',
      size: 'Web App',
      downloads: 3200
    },
    {
      id: 8,
      title: 'Options Pricing Model',
      category: 'tools',
      type: 'Excel Tool',
      description: 'Black-Scholes and Binomial tree models for options pricing and Greeks calculation.',
      downloadUrl: '#',
      size: '0.8 MB',
      downloads: 640
    },

    // Articles  
    {
      id: 9,
      title: 'Career Transition Guide: From Engineering to Finance',
      category: 'articles',
      type: 'Blog Article',
      description: 'Step-by-step guide for engineers looking to break into finance careers.',
      downloadUrl: '#',
      size: 'Read Online',
      downloads: 890
    },
    {
      id: 10,
      title: 'Top 10 Finance Interview Questions',
      category: 'articles',
      type: 'Blog Article', 
      description: 'Most commonly asked finance interview questions with detailed answers.',
      downloadUrl: '#',
      size: 'Read Online',
      downloads: 1540
    }
  ];

  const filteredResources = activeCategory === 'all' 
    ? resources 
    : resources.filter(resource => resource.category === activeCategory);

  const featuredResources = [
    {
      title: 'Master Financial Modeling',
      description: 'Complete toolkit for building professional-grade financial models',
      items: 4,
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
    },
    {
      title: 'Excel Templates Collection',
      description: 'Ready-to-use templates for valuation, analysis, and reporting',
      items: 8,
      image: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74'
    },
    {
      title: 'Career Transition Guides',
      description: 'Everything you need to successfully transition into finance',
      items: 6,
      image: 'https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg'
    }
  ];

  return (
    <div className="resources-page" data-testid="resources-page">
      {/* Hero Section */}
      <section className="hero" style={{ minHeight: '60vh' }}>
        <div className="container">
          <div className="hero-content animate-fade-in-up text-center">
            <h1 className="heading-primary mb-lg">Free Finance Resources</h1>
            <p className="text-large mb-xl" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto 2rem' }}>
              Access our comprehensive collection of guides, templates, tools, and articles to accelerate your finance learning journey.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="section" style={{ background: 'var(--surface)' }}>
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Featured Collections</h2>
          
          <div className="grid grid-3 gap-xl">
            {featuredResources.map((collection, index) => (
              <div key={index} className="card text-center" data-testid={`featured-collection-${index}`}>
                <img 
                  src={collection.image}
                  alt={collection.title}
                  style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '1rem', marginBottom: '1.5rem' }}
                />
                <h3 className="heading-tertiary mb-md" style={{ fontSize: '1.25rem' }}>{collection.title}</h3>
                <p className="text-base mb-md" style={{ color: 'var(--text-secondary)' }}>{collection.description}</p>
                <div className="text-sm" style={{ color: 'var(--secondary)', fontWeight: '600' }}>
                  {collection.items} Resources Available
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resource Library */}
      <section className="section">
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Resource Library</h2>
          
          {/* Category Filter */}
          <div className="category-filters mb-2xl">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                data-testid={`filter-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Resources Grid */}
          <div className="grid grid-2 gap-lg">
            {filteredResources.map(resource => (
              <div key={resource.id} className="card" data-testid={`resource-${resource.id}`}>
                <div className="flex justify-between items-start mb-md">
                  <div>
                    <h3 className="heading-tertiary mb-sm" style={{ fontSize: '1.125rem' }}>{resource.title}</h3>
                    <div className="flex gap-md items-center mb-sm">
                      <span className="resource-type">{resource.type}</span>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Size: {resource.size}</span>
                    </div>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--secondary)', fontWeight: '600' }}>
                    {resource.downloads} downloads
                  </div>
                </div>
                
                <p className="text-base mb-lg" style={{ color: 'var(--text-secondary)' }}>
                  {resource.description}
                </p>
                
                <div className="flex gap-md">
                  <a 
                    href={resource.downloadUrl}
                    className="btn btn-primary"
                    data-testid={`download-${resource.id}`}
                  >
                    Download
                  </a>
                  <button className="btn btn-outline" data-testid={`preview-${resource.id}`}>
                    Preview
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="section" style={{ background: 'var(--primary)', color: 'white' }}>
        <div className="container">
          <div className="text-center">
            <h2 className="heading-secondary mb-md" style={{ color: 'white' }}>
              Stay Updated with Latest Resources
            </h2>
            <p className="text-large mb-xl" style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Subscribe to our newsletter and get notified when we add new templates, guides, and tools to our resource library.
            </p>
            
            <div className="newsletter-form" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div className="flex gap-md">
                <input 
                  type="email" 
                  placeholder="Enter your email address"
                  className="form-input"
                  style={{ flex: 1 }}
                  data-testid="newsletter-email-input"
                />
                <button className="btn btn-secondary" data-testid="subscribe-newsletter-btn">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .category-filters {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .filter-btn {
          padding: 0.75rem 1.5rem;
          border: 2px solid var(--border);
          background: var(--background);
          color: var(--text-primary);
          border-radius: 2rem;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .filter-btn:hover {
          border-color: var(--secondary);
          color: var(--secondary);
        }

        .filter-btn.active {
          background: var(--secondary);
          border-color: var(--secondary);
          color: white;
        }

        .resource-type {
          background: var(--surface);
          color: var(--secondary);
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .newsletter-form input {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .newsletter-form input::placeholder {
          color: rgba(255, 255, 255, 0.7);
        }

        .newsletter-form input:focus {
          border-color: var(--secondary);
          background: rgba(255, 255, 255, 0.15);
        }

        @media (max-width: 768px) {
          .category-filters {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 0.5rem;
          }

          .filter-btn {
            flex-shrink: 0;
          }

          .newsletter-form .flex {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default Resources;